import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import { gr } from "../tokens";
import icons from "../icons";
import GrIcon from "./GrIcon";
import GrIconTile from "./GrIconTile";
import GrStatusPill from "./GrStatusPill";

// Task types shown in the Daily Action Plan: icon + plain label.
export const grTaskTypes = {
  workout: { label: "Workout", icon: icons.taskWorkout },
  meal: { label: "Meal", icon: icons.taskMeal },
  mindset: { label: "Mindset", icon: icons.taskMindset },
  workbook: { label: "Workbook", icon: icons.taskWorkbook },
  video: { label: "Video", icon: icons.taskVideo },
  comment: { label: "Note", icon: icons.taskComment },
};

const ellipsis = {
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

// One task as a full-width button row: type icon, "Type · Programme", task title, meta, Done pill, chevron.
// Rows stacked in a list get a hairline between them.
export default function GrTaskRow({
  type = "workout",
  title,
  programme,
  meta,
  done = false,
  onClick,
  sx,
}) {
  const t = grTaskTypes[type] || grTaskTypes.workout;
  return (
    <ButtonBase
      onClick={onClick}
      focusRipple
      sx={{
        width: "100%",
        display: "flex",
        alignItems: "center",
        gap: gr.space[3],
        p: gr.space[3],
        textAlign: "left",
        borderRadius: gr.radius.tile,
        fontFamily: gr.font.family,
        transition: "background .15s ease",
        "&:hover": { background: gr.color.tint },
        "&.Mui-focusVisible": { boxShadow: gr.shadow.focus },
        ...sx,
      }}
    >
      <GrIconTile size="sm" tone={done ? "ok" : "brand"}>
        <GrIcon icon={t.icon} />
      </GrIconTile>
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          gap: "2px",
        }}
      >
        <Box
          component="span"
          sx={{ ...ellipsis, fontSize: gr.font.size.xs, color: gr.color.muted }}
        >
          {t.label}
          {programme ? ` · ${programme}` : ""}
        </Box>
        <Box
          component="span"
          sx={{
            ...ellipsis,
            fontSize: gr.font.size.md,
            fontWeight: gr.font.weight.semibold,
            color: gr.color.ink,
          }}
        >
          {title}
        </Box>
        {meta ? (
          <Box
            component="span"
            sx={{
              ...ellipsis,
              fontSize: gr.font.size.sm,
              color: gr.color.ink2,
            }}
          >
            {meta}
          </Box>
        ) : null}
      </Box>
      {done ? <GrStatusPill status="ok">Done</GrStatusPill> : null}
      <Box
        component="span"
        aria-hidden
        sx={{ display: "flex", color: gr.color.muted }}
      >
        <GrIcon icon={icons.chevronRight} />
      </Box>
    </ButtonBase>
  );
}

GrTaskRow.propTypes = {
  type: PropTypes.oneOf(Object.keys(grTaskTypes)),
  title: PropTypes.node.isRequired,
  programme: PropTypes.node,
  meta: PropTypes.node,
  done: PropTypes.bool,
  onClick: PropTypes.func,
  sx: PropTypes.object,
};
